// ============================================
// @narbodica/auth — onboarding/NarbodicaSync.jsx
// ============================================
// Shown during onboarding when the user has
// existing profiles in other Narbodica apps.
// Presents what we already know and asks
// explicit permission to use it.
//
// Privacy principle: never import without
// explicit consent at this exact moment.
// Per-registration permission, not global.
// ============================================

import { useState } from 'react';

const API = (base, path) => `${base}${path}`;

const APP_LABELS = {
  spark: { label: 'Spark', color: '#8b5cf6' },
  connect: { label: 'Connect', color: '#14b8a6' },
  maia: { label: 'Maia', color: '#a78bfa' },
  eros: { label: 'Eros', color: '#ef4444' },
  artemis: { label: 'Artemis', color: '#c2713a' },
};

// Human-readable field labels
const FIELD_LABELS = {
  name: 'Your name',
  dateOfBirth: 'Date of birth',
  gender: 'Gender',
  location: 'Location',
  'location.city': 'City',
  'location.state': 'State',
  interests: 'Interests',
  occupation: 'Occupation',
  bio: 'About you',
  education: 'Education',
  'dating.currentStatus': 'Relationship status',
  'identity.lifeStage': 'Life stage',
  'identity.occupation': 'Occupation',
  photos: 'Profile photo',
};

const fieldLabel = (key) => FIELD_LABELS[key] || key.replace(/([A-Z])/g, ' $1').replace(/\./g, ' — ').toLowerCase();

export default function NarbodicaSync({
  app,
  appLabel,
  accentColor,
  token,
  apiBase,
  syncData,
  onAccept,
  onDecline,
}) {
  const [importing, setImporting] = useState(false);
  const [selected, setSelected] = useState(() => {
    // Default: all fields selected
    const init = {};
    if (syncData?.fields) {
      syncData.fields.forEach(f => { init[f.key] = true; });
    }
    return init;
  });

  const fields = syncData?.fields || [];
  const sources = [...new Set(fields.map(f => f.sourceApp))];

  const handleAccept = async () => {
    setImporting(true);
    try {
      const selectedFields = fields.filter(f => selected[f.key]);
      if (selectedFields.length > 0) {
        await fetch(API(apiBase, '/api/cross-app/import-merged'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            destinationApp: app,
            fields: selectedFields,
            consentTimestamp: new Date().toISOString(),
          }),
        });
      }
    } catch (e) {
      console.error('Sync import failed:', e);
    }
    setImporting(false);
    onAccept();
  };

  const toggleField = (key) => {
    setSelected(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '32px 24px',
    }}>
      <div style={{ width: '100%', maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 24 }}>

        {/* Header */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔗</div>
          <h2 style={{ color: '#fafafa', fontSize: 24, fontFamily: "var(--font-serif)", margin: '0 0 8px' }}>
            You're already with us
          </h2>
          <p style={{ color: '#71717a', fontSize: 14, margin: 0, lineHeight: 1.6 }}>
            {`You already use ${sources.map(s => APP_LABELS[s]?.label || s).join(' and ')} with us. Want us to pre-fill ${appLabel} with what we already know? You can choose exactly what to bring across.`}
          </p>
        </div>

        {/* Source app badges */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {sources.map(s => (
            <span key={s} style={{
              padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
              background: `${APP_LABELS[s]?.color || '#52525b'}20`,
              color: APP_LABELS[s]?.color || '#a1a1aa',
              border: `1px solid ${APP_LABELS[s]?.color || '#52525b'}40`,
            }}>
              {APP_LABELS[s]?.label || s}
            </span>
          ))}
        </div>

        {/* Field checklist */}
        <div style={{
          background: '#18181b', border: '1px solid #27272a',
          borderRadius: 16, overflow: 'hidden',
        }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #27272a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#a1a1aa', fontSize: 13, fontWeight: 600 }}>What to bring across</span>
            <button
              onClick={() => {
                const allSelected = selectedCount === fields.length;
                const next = {};
                fields.forEach(f => { next[f.key] = !allSelected; });
                setSelected(next);
              }}
              style={{ background: 'none', border: 'none', color: accentColor, fontSize: 12, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
            >
              {selectedCount === fields.length ? 'Deselect all' : 'Select all'}
            </button>
          </div>
          {fields.map((field, i) => (
            <div
              key={field.key}
              onClick={() => toggleField(field.key)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 20px', cursor: 'pointer',
                borderBottom: i < fields.length - 1 ? '1px solid #18181b' : 'none',
                background: selected[field.key] ? `${accentColor}08` : 'transparent',
                transition: 'background 0.15s',
              }}
            >
              <div>
                <p style={{ color: '#fafafa', fontSize: 14, margin: 0, fontWeight: 500 }}>{fieldLabel(field.key)}</p>
                <p style={{ color: '#71717a', fontSize: 12, margin: '2px 0 0' }}>
                  {field.displayValue || String(field.value).slice(0, 40)}
                  {' · '}
                  <span style={{ color: APP_LABELS[field.sourceApp]?.color || '#71717a' }}>
                    from {APP_LABELS[field.sourceApp]?.label || field.sourceApp}
                  </span>
                </p>
              </div>
              <div style={{
                width: 20, height: 20, borderRadius: 6,
                border: `2px solid ${selected[field.key] ? accentColor : '#3f3f46'}`,
                background: selected[field.key] ? accentColor : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
                transition: 'all 0.15s',
              }}>
                {selected[field.key] && (
                  <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                    <path d="M1 4L4 7L10 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Privacy note */}
        <p style={{ color: '#3f3f46', fontSize: 11, textAlign: 'center', margin: 0 }}>
          This is a one-time choice. We'll only import what you select here. You can always update your profile in settings.
        </p>

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            onClick={handleAccept}
            disabled={importing}
            style={{
              padding: '14px', background: selectedCount > 0 ? accentColor : '#27272a',
              border: 'none', borderRadius: 12, color: selectedCount > 0 ? '#fff' : '#52525b',
              fontSize: 14, fontWeight: 600, cursor: importing ? 'wait' : 'pointer',
              fontFamily: "'Inter', sans-serif",
            }}
          >
            {importing ? 'Importing...' : selectedCount > 0 ? `Use ${selectedCount} field${selectedCount !== 1 ? 's' : ''}` : 'Continue without importing'}
          </button>
          <button
            onClick={onDecline}
            style={{
              padding: '12px', background: 'none', border: 'none',
              color: '#52525b', fontSize: 13, cursor: 'pointer',
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Start fresh — I'll fill everything in myself
          </button>
        </div>
      </div>
    </div>
  );
}
